import React from 'react';
import { useNavigate } from 'react-router-dom';
import { TriangleIcon, CheckIcon, ArrowRightIcon } from 'lucide-react';
import { CRESTMONT, CrestmontHeader } from '../components/crestmont/CrestmontChrome';
import { Footer } from '../components/Footer';
import { CrestmontLeadForm } from '../components/crestmont/CrestmontLeadForm';
import { BenefitsSection, CtaBand, FaqSection } from '../components/crestmont/CrestmontDetails';
import { crestmontPrograms } from '../data/crestmontPrograms';
import { getUniversityBySlug } from '../data/universityData';
import { useMetaTags } from '../hooks/useMetaTags';

// Institution-level Crestmont University page, built to the same brand guide
// as the degree pages. Marketing copy comes from the existing Crestmont
// advertiser record so the two stay in sync; the degree cards deep-link into
// the BBA and AA-BA pages.
export function CrestmontUniversityPage() {
  const navigate = useNavigate();
  const university = getUniversityBySlug('crestmont-university');
  const content = university?.landingContent;
  // The site footer navigates by page key, matching the rest of the site.
  const handleNavigate = (page: string) => {
    navigate(page === 'compare' ? '/' : `/${page}`);
    window.scrollTo(0, 0);
  };
  useMetaTags({
    title: 'Crestmont University — Online Degrees Built Around Your Life',
    description: content?.subheadline || 'Accredited online degree programs from Crestmont University.',
    robots: 'noindex, follow'
  });
  const scrollToForm = () => {
    const target = document.getElementById('apply');
    if (target) target.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });
  };
  const degrees = Object.values(crestmontPrograms);
  const programOptions = degrees.map(entry => ({
    label: entry.name,
    value: entry.mProgramValue
  }));
  return <div className="min-h-screen bg-white w-full" data-id="element-1708" data-mp="3etdutpldbcrl">
      <CrestmontHeader onApply={scrollToForm} data-id="element-1709" data-mp="3js8bolzryr92" />

      <main data-id="element-1710" data-mp="3jqx7il2f3hhf">
        {/* Hero */}
        <section className="py-14 md:py-20 border-b border-gray-200" style={{
        backgroundColor: CRESTMONT.mist
      }} data-id="element-1711" data-mp="1pb8bt3y4dmsp">
          <div className="max-w-6xl mx-auto px-4 grid gap-10 lg:grid-cols-5 lg:items-start" data-id="element-1712" data-mp="1kjp7hbwz7yqf">
            <div className="lg:col-span-3" data-id="element-1713" data-mp="1gidbmonieh9s">
              
              <h1 className="text-4xl md:text-5xl font-extrabold leading-[1.05] tracking-tight max-w-lg" style={{
              color: CRESTMONT.ink
            }} data-id="element-1714" data-mp="1ggsnbgrsfukd">
                Education tailored to your life.
              </h1>
              <p className="mt-5 text-sm md:text-base text-gray-600 max-w-lg leading-relaxed" data-id="element-1715" data-mp="2487np40081sz">
                {content?.subheadline}
              </p>
              <ul className="mt-8 space-y-2.5 max-w-lg" data-id="element-1716" data-mp="1zbr3ulzu89lp">
                {(content?.heroHighlights || []).map(highlight => <li key={highlight} className="flex items-start gap-2.5" data-id="element-1717" data-mp="1ucxnmlt624hu">
                    <CheckIcon className="mt-0.5 w-4 h-4 flex-shrink-0" style={{
                  color: CRESTMONT.navy
                }} aria-hidden="true" data-id="element-1718" data-mp="nw2c7hbn2mek" />
                    <span className="text-sm leading-relaxed text-gray-700" data-id="element-1719" data-mp="8s92hj8vexyo">
                      {highlight}
                    </span>
                  </li>)}
              </ul>
            </div>
            <div className="lg:col-span-2" data-id="element-1720" data-mp="ip3ztyteqsbd">
              <CrestmontLeadForm programValue={degrees[0].mProgramValue} programName="program you choose" programOptions={programOptions} heading="Request Program Information" subheading="Tell us where to reach you and a Crestmont advisor will follow up with program details, transfer credit, and next steps." data-id="element-1721" data-mp="iocerhxfsczc" />
            </div>
          </div>
        </section>

        {/* Stats strip */}
        {content?.stats && content.stats.length > 0 && <section className="bg-white border-b border-gray-200" aria-label="Crestmont at a glance" data-id="element-1722" data-mp="16eycaf9teev3">
            <dl className="max-w-6xl mx-auto px-4 py-8 grid grid-cols-2 md:grid-cols-4 gap-6" data-id="element-1723" data-mp="337rtaydmp0w5">
              {content.stats.map(stat => <div key={stat.label} className="text-center" data-id="element-1724" data-mp="385td5livp48o">
                  <dt className="sr-only" data-id="element-1725" data-mp="12iar3mr3rtki">{stat.label}</dt>
                  <dd data-id="element-1726" data-mp="12gr8hphz56fk">
                    <span className="block text-2xl font-extrabold" style={{
                color: CRESTMONT.navy
              }} data-id="element-1727" data-mp="12kf1mnjibqtr">
                      {stat.value}
                    </span>
                    <span className="mt-1 block text-[11px] text-gray-500" data-id="element-1728" data-mp="1bgwga0r2y6u9">
                      {stat.label}
                    </span>
                  </dd>
                </div>)}
            </dl>
          </section>}

        {/* Benefits + accreditation — placed under the hero so the credibility
            signals land before the longer institutional detail. */}
        <BenefitsSection benefits={degrees[0].benefits} data-id="element-1729" data-mp="skwmqligev8k" />

        {/* Degree programs — the primary decision on this page */}
        <section className="py-16 md:py-20 bg-white" aria-labelledby="degrees-heading" data-id="element-1730" data-mp="ns3evorov6s9">
          <div className="max-w-5xl mx-auto px-4" data-id="element-1731" data-mp="2937aj3vo9l">
            <h2 id="degrees-heading" className="text-3xl md:text-4xl font-extrabold tracking-tight" style={{
            color: CRESTMONT.navy
          }} data-id="element-1732" data-mp="3raof68kfmoap">
              Choose your business degree.
            </h2>
            <p className="mt-3 text-sm text-gray-600 max-w-xl" data-id="element-1733" data-mp="2bj2qm45wybcd">
              Both degrees run one course at a time in five-week terms. Every
              associate credit transfers into the bachelor’s, so starting
              smaller costs you nothing.
            </p>

            <ul className="mt-10 grid gap-5 md:grid-cols-2" data-id="element-1734" data-mp="2qgcxf07m2t1r">
              {degrees.map(degree => <li key={degree.slug} className="flex" data-id="element-1735" data-mp="2uhoozba1pocl">
                  <a href={`/universities/crestmont-university/${degree.slug}`} className="group flex flex-col w-full border border-gray-200 rounded-lg p-6 transition-colors hover:border-[#00426b] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00426b]" data-id="element-1736" data-mp="2ugdih3p7u2hs">
                    <span className="inline-flex self-start px-2.5 py-1 rounded text-[10px] font-bold uppercase tracking-wide" style={{
                  backgroundColor: CRESTMONT.mist,
                  color: CRESTMONT.navy
                }} data-id="element-1737" data-mp="1527bopp6gb4c">
                      {degree.degreeLevel}
                    </span>
                    <h3 className="mt-4 text-lg font-bold leading-snug" style={{
                  color: CRESTMONT.navy
                }} data-id="element-1738" data-mp="lz6nnp3rk2qk">
                      {degree.name}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-gray-600" data-id="element-1739" data-mp="c797e3xwekkz">
                      {degree.heroSubhead}
                    </p>
                    <dl className="mt-5 flex flex-wrap gap-x-6 gap-y-2" data-id="element-1740" data-mp="76v2gurlfqr6">
                      <div data-id="element-1741" data-mp="28thg4alxhd2">
                        <dt className="text-[10px] uppercase tracking-wide text-gray-400" data-id="element-1742" data-mp="3udxdnx4opds8">
                          Credits
                        </dt>
                        <dd className="text-sm font-bold tabular-nums" style={{
                      color: CRESTMONT.navy
                    }} data-id="element-1743" data-mp="3pg5kz0w7tnmz">
                          {degree.curriculum.totalCredits}
                        </dd>
                      </div>
                      <div data-id="element-1744" data-mp="1v7iamx8eh666">
                        <dt className="text-[10px] uppercase tracking-wide text-gray-400" data-id="element-1745" data-mp="1v6778yiva0fl">
                          Standard plan
                        </dt>
                        <dd className="text-sm font-bold" style={{
                      color: CRESTMONT.navy
                    }} data-id="element-1746" data-mp="24x1tw400z21k">
                          {degree.stats[0].value}
                        </dd>
                      </div>
                      <div data-id="element-1747" data-mp="1c9fqowuplzf8">
                        <dt className="text-[10px] uppercase tracking-wide text-gray-400" data-id="element-1748" data-mp="1ca8r7g32rsox">
                          Total tuition
                        </dt>
                        <dd className="text-sm font-bold tabular-nums" style={{
                      color: CRESTMONT.navy
                    }} data-id="element-1749" data-mp="1m58zc55b58rq">
                          {degree.requirements.tuition.total}
                        </dd>
                      </div>
                    </dl>
                    <span className="mt-auto pt-5 border-t border-gray-100 inline-flex items-center gap-1.5 text-sm font-bold" style={{
                  color: CRESTMONT.navy
                }} data-id="element-1750" data-mp="31mau3c57sl23">
                      Explore the {degree.shortName}
                      <ArrowRightIcon className="w-4 h-4 transition-transform duration-200 ease-out group-hover:translate-x-0.5" aria-hidden="true" data-id="element-1751" data-mp="3inf0nway6e9e" />
                    </span>
                  </a>
                </li>)}
            </ul>
          </div>
        </section>

        {/* Why Crestmont */}
        {content?.features && content.features.length > 0 && <section className="py-16 md:py-20" style={{
        backgroundColor: CRESTMONT.mist
      }} aria-labelledby="why-heading" data-id="element-1752" data-mp="5beku7iq6pjd">
            <div className="max-w-5xl mx-auto px-4" data-id="element-1753" data-mp="k74biiyuvquf">
              <h2 id="why-heading" className="text-2xl md:text-3xl font-extrabold tracking-tight" style={{
            color: CRESTMONT.navy
          }} data-id="element-1754" data-mp="f89fugfsi7sw">
                Why students choose Crestmont
              </h2>
              <ul className="mt-8 grid gap-4 sm:grid-cols-2" data-id="element-1755" data-mp="2h17eisw8zulr">
                {content.features.map(feature => <li key={feature.title} className="bg-white rounded-lg p-6 border-l-2" style={{
              borderLeftColor: CRESTMONT.gold
            }} data-id="element-1756" data-mp="wj6eq51aldgs">
                    <h3 className="text-sm font-bold" style={{
                color: CRESTMONT.navy
              }} data-id="element-1757" data-mp="msbu153p2lxg">
                      {feature.title}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-gray-600" data-id="element-1758" data-mp="rp267hcdoxdl">
                      {feature.description}
                    </p>
                  </li>)}
              </ul>
            </div>
          </section>}

        {/* Long-form institutional content */}
        {content?.infoSections && content.infoSections.length > 0 && <section className="py-16 md:py-20 bg-white" data-id="element-1759" data-mp="8s10fub02t8l">
            <div className="max-w-3xl mx-auto px-4 space-y-12" data-id="element-1760" data-mp="8un6fpi8t0op">
              {content.infoSections.map(section => <article key={section.heading} data-id="element-1761" data-mp="1y5e6bnyrgpbt">
                  {section.eyebrow && <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#00426b]/60" data-id="element-1762" data-mp="23590c0du1v13">
                      {section.eyebrow}
                    </p>}
                  <h2 className="mt-2 text-xl md:text-2xl font-extrabold tracking-tight" style={{
              color: CRESTMONT.navy
            }} data-id="element-1763" data-mp="2kcsvnh0x9o4e">
                    {section.heading}
                  </h2>
                  <p className="mt-3 text-sm leading-relaxed text-gray-600" data-id="element-1764" data-mp="2bj64k47nt91j">
                    {section.body}
                  </p>
                  {section.bullets && <ul className="mt-4 space-y-2" data-id="element-1765" data-mp="2z7pawe75fbqz">
                      {section.bullets.map(bullet => <li key={bullet} className="flex items-start gap-2.5" data-id="element-1766" data-mp="33ac68p313esx">
                          <CheckIcon className="mt-0.5 w-4 h-4 flex-shrink-0" style={{
                  color: CRESTMONT.navy
                }} aria-hidden="true" data-id="element-1767" data-mp="2pakms8tqt67j" />
                          <span className="text-sm leading-relaxed text-gray-700" data-id="element-1768" data-mp="1dvntunuftwuw">
                            {bullet}
                          </span>
                        </li>)}
                    </ul>}
                </article>)}
            </div>
          </section>}

        <CtaBand heading={content?.finalCta?.headline || 'Interested in learning more about our degrees?'} body={content?.finalCta?.body || ''} onApply={scrollToForm} data-id="element-1769" data-mp="1bdltsy3q35em" />
        <FaqSection faq={degrees[0].faq} data-id="element-1770" data-mp="1kbxgwq5drl2b" />
      </main>

      <Footer onNavigate={handleNavigate} data-id="element-1771" data-mp="1fdm90kynbkmq" />
    </div>;
}